import { mapActions, mapState, mapGetters } from 'vuex';
import InsightsTracking from './InsightsTrackingMixin';
import { makeUserTimeZone } from '~/utils/time_utils';

// Constants for tab names to avoid magic strings
const TABS = {
  TIMELINE: 'timeline',
  UNUSUAL_ACTIVITIES: 'unusual-activities',
};

export default {
  mixins: [InsightsTracking],
  data: () => ({
    filterSettings: Object.freeze({
      allowClear: false,
      multiple: false,
      placeholder: 'Loading...',
      minimumResultsForSearch: 8,
    }),
    dateFilterTimeout: null,
  }),
  watch: {
    users(val) {
      if (val.length) {
        this.setTimeZoneFilter();
      }
    },
  },
  computed: {
    ...mapState(['organization', 'filterQuery', 'filters', 'selectedFilter', 'previousView']),
    ...mapGetters([
      'isEverythingReady',
      'users',
      'teams',
      'timeZoneOptions',
      'activeTimeZone',
      'dashboardType',
      'isOwner',
      'isTeamLead',
      'isManager',
      'previousDashboardIsIndividualTeam',
      'currentUserBelongsToPrevTeam',
      'previousDashboardTeamName',
    ]),
    backButtonText() {
      let text = 'All members';

      if (this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam) {
        text = this.previousDashboardTeamName;
      }

      return text;
    },
    /**
     * Determines if widget reloading should be skipped for filter changes.
     * Currently, the timeline tab manages its own widget loading state,
     * so we skip global widget reload to prevent UI conflicts.
     */
    shouldSkipWidgetReload() {
      return this.filters.tab === TABS.TIMELINE;
    },
  },
  methods: {
    ...mapActions([
      'setTimeZoneOptions',
      'setActiveTimeZone',
      'setFilter',
      'setShowBenchmarksAverages',
      'fetchEngagementScores',
      'setFilterQueryValue',
      'setPreviousView',
      'setSelectedFilter',
      'setStartsAtFilter',
      'setActivityTypeFilter',
      'setTimelineFilter',
      'setConfidenceLevelsFilter',
    ]),
    initFilters() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      if (urlParams.get('to') && urlParams.get('from')) {
        const date = {
          to: urlParams.get('to'),
          from: urlParams.get('from'),
        };

        this.setFilterQueryValue({ key: 'date', value: date });
      }
      if (urlParams.has('user_id')) {
        this.setFilterQueryValue({ key: 'user_id', value: urlParams.get('user_id') });
      }
      if (urlParams.has('confidence_levels')) {
        const confidenceLevels = urlParams.get('confidence_levels').split(',');

        this.setFilterQueryValue({ key: 'confidence_levels', value: confidenceLevels });
      }
      if (urlParams.has('time_zone')) {
        this.setFilterQueryValue({ key: 'time_zone', value: urlParams.get('time_zone') });
        const activeZone = this.timeZoneOptions.find((tz) => tz.for === urlParams.get('time_zone'));

        if (activeZone) {
          this.setActiveTimeZone(activeZone);
        }
      }
    },
    setDateFilter({ query }) {
      // Update the local filter query immediately for UI responsiveness
      this.setFilterQueryValue({ key: 'date', value: query.value });

      // Clear any existing timeout
      if (this.dateFilterTimeout) {
        clearTimeout(this.dateFilterTimeout);
      }

      // Debounce the global filter updates and API calls
      this.dateFilterTimeout = setTimeout(() => {
        this.setTimeZoneFilter();
        // Update global filter state for all tabs
        // Skip widget reload on timeline tab since it manages its own loading state
        // Also skip on initial mount to prevent double-fetching with TheInsights created hook
        this.setFilter({
          key: 'date',
          value: query.value,
          filterQuery: this.filterQuery,
          multiple: true,
          skipWidgets: !!(this.shouldSkipWidgetReload || query.initial),
        });

        // Only fetch engagement scores when navigation sidebar is visible
        // (engagement scores are only displayed in the navigation sidebar, which is hidden on timeline tab)
        if (this.filters.tab !== TABS.TIMELINE) {
          this.fetchEngagementScores();
        }
      }, 300);

      // Analytics tracking (keep immediate for accurate user interaction tracking)
      // Skip tracking when:
      // 1. query.initial is true (initial load)
      // 2. query.skipTracking is true (implicit date change from member/team selection)
      if (!query.initial && !query.skipTracking) {
        const to = new Date(query.value.to).getTime();
        const from = new Date(query.value.from).getTime();

        this.trackAnalytics('Insights date apply', {
          days_in_range: (to - from) / (1000 * 3600 * 24),
          preset_value: query.value.id,
          type: 'Date picker',
          statsigCustomIDs: ['organizationID', this.organization.id],
        });
      }
    },

    setMembersWithPreviousView(string, teamId = null) {
      this.setSelectedFilter(string);
      this.setPreviousView({ previousView: teamId || this.selectedFilter, currentView: string });
      // Give time to the team filter to settle setting the members filter
      this.$nextTick(() => {
        this.setMembersFilter(string);
      });
    },

    setMembersFilter(member) {
      let initialValue;
      let initialFilterType;

      if (this.filterQuery.user_id) {
        initialValue = String(this.filterQuery.user_id);
        initialFilterType = 'user';
      } else if (this.filterQuery.team_id) {
        initialValue = String(this.filterQuery.team_id);
        initialFilterType = 'team';
      } else {
        initialValue = initialFilterType = 'all';
      }

      const [id, prefix] = [...member.split(/_(.*)/s)];

      this.setFilterQueryValue({ key: prefix, value: id });
      this.setFilterQueryValue({ key: prefix === 'user_id' ? 'team_id' : 'user_id', value: null });

      this.setTimeZoneFilter();

      this.setFilter({
        filterQuery: this.filterQuery,
        multiple: true,
        skipWidgets: this.shouldSkipWidgetReload,
      });

      let event;

      switch (this.filters.tab) {
        case TABS.UNUSUAL_ACTIVITIES:
          event = 'Unusual filter changed';
          break;
        case TABS.TIMELINE:
          event = 'Timeline filter changed';
          break;
        default:
          event = 'Insights filters changed';
      }

      this.trackAnalytics(event, {
        initial_value: initialValue,
        initial_filter_type: initialFilterType,
        new_value: id,
        filter_type: id === 'all' ? 'all' : prefix.split('_')[0],
      });
    },
    setTimeZoneFilter() {
      const from = this.filterQuery.date?.from ? moment(this.filterQuery.date.from) : null;
      const to = this.filterQuery.date?.to ? moment(this.filterQuery.date.to) : null;
      const isDayPeriod = from && to ? from.isSame(to, 'day') : false;
      const userId = this.filterQuery.user_id;
      const user = this.users.find((user) => user.id === +userId);

      if (isDayPeriod && user) {
        const previouslyActiveTimeZone = this.activeTimeZone;

        // Normalize user time zone if the user was selected from the dropdown
        if (typeof user.time_zone === 'string') {
          user.time_zone = makeUserTimeZone(user.name, user.time_zone);
        }

        this.setTimeZoneOptions([this.organization.time_zone, user.time_zone]);

        if (previouslyActiveTimeZone.for !== user.time_zone.for) {
          const activeZone = this.timeZoneOptions.find((tz) => tz.for === user.time_zone.for);

          this.setFilterQueryValue({ key: 'time_zone', value: user.time_zone.for });
          this.setActiveTimeZone(activeZone);
        }
      } else if (isDayPeriod && !user) {
        /**
         *  timeZoneOptions object contains [org tz, member's tz]
         *  We pick the last one as default
         */
        const activeZone = this.timeZoneOptions[this.isOwner || this.isManager || this.isTeamLead ? 0 : 1];

        if (this.activeTimeZone.for !== activeZone.for || this.filterQuery.time_zone !== activeZone.for) {
          this.setFilterQueryValue({ key: 'time_zone', value: activeZone.for });
          this.setActiveTimeZone(activeZone);
        }
      } else {
        const activeZone = this.timeZoneOptions.find((tz) => tz.for === this.organization.time_zone.for);

        if (this.activeTimeZone.for !== activeZone.for || this.filterQuery.time_zone !== activeZone.for) {
          this.setFilterQueryValue({ key: 'time_zone', value: this.organization.time_zone.for });
          this.setActiveTimeZone(activeZone);
        }
      }
    },

    changeTimeZone(tzParam) {
      const activeZone = this.timeZoneOptions.find((tz) => tz.for === tzParam);

      if (this.activeTimeZone.for !== activeZone.for || this.filterQuery.time_zone !== tzParam) {
        this.setFilterQueryValue({ key: 'time_zone', value: tzParam });
        this.setActiveTimeZone(activeZone);

        this.setFilter({
          filterQuery: this.filterQuery,
          multiple: true,
          skipWidgets: this.shouldSkipWidgetReload,
        });
      }
    },

    setStartsAtFilter(date) {
      this.setFilterQueryValue({ key: 'starts_at', value: date });

      this.setFilter({ filterQuery: this.filterQuery, multiple: true });
    },

    setActivityTypeFilter(activityType) {
      this.setFilterQueryValue({ key: 'activity_type', value: activityType });

      this.setFilter({ filterQuery: this.filterQuery, multiple: true });
    },

    setConfidenceLevelsFilter(confidenceLevels) {
      this.setFilterQueryValue({ key: 'confidence_levels', value: confidenceLevels });

      this.setFilter({ filterQuery: this.filterQuery, multiple: true });
    },

    backButtonLink() {
      let string = 'all_user_id';

      if (this.previousDashboardIsIndividualTeam && this.currentUserBelongsToPrevTeam) {
        string = this.previousView;
      }

      this.setPreviousView({ previousView: null, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
  },
};
